import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { AlertTriangle, Check, X, ExternalLink, Loader2, RefreshCw, Keyboard } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * BulkAttributionReviewPage — power-user cleanup surface for the queue
 * of open speaker-vs-registered-agent mismatches. Keyboard shortcuts:
 *   J / ↓ — next flag
 *   K / ↑ — previous flag
 *   R    — accept the suggested reassignment on the focused flag
 *   D    — dismiss the focused flag ("keep as is")
 *   O    — open the underlying call in a new tab
 *
 * Renders every row inline so a manager can scan + clear the whole
 * backlog in minutes.
 */
const _fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }); }
  catch { return String(iso); }
};

const BulkAttributionReviewPage = () => {
  const [flags, setFlags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [focusIdx, setFocusIdx] = useState(0);
  const rowRefs = useRef({});

  const load = useCallback(() => {
    setLoading(true);
    axios.get(`${API}/admin/attribution-flags?limit=200`, { timeout: 20000 })
      .then((r) => setFlags(r.data?.flags || []))
      .catch(() => setFlags([]))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  // Scroll focused row into view.
  useEffect(() => {
    const key = flags[focusIdx]?.call_id;
    if (key && rowRefs.current[key]) {
      rowRefs.current[key].scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }, [focusIdx, flags]);

  const removeFromList = (call_id) => {
    setFlags((prev) => {
      const next = prev.filter((f) => f.call_id !== call_id);
      // Keep focus reasonable after a removal.
      setFocusIdx((i) => Math.min(i, Math.max(0, next.length - 1)));
      return next;
    });
  };

  const reassignSuggested = useCallback(async (flag) => {
    if (!flag?.suggested_agent_id) {
      toast.error('No suggested user for this flag — open the call to pick one manually');
      return;
    }
    setBusyId(flag.call_id);
    try {
      await axios.post(
        `${API}/calls/${flag.call_id}/reassign-agent`,
        {
          agent_id: flag.suggested_agent_id,
          reason: `Bulk review — accepted suggestion for spoken name "${flag.detected_name}"`,
        },
        { timeout: 15000 },
      );
      toast.success(`Reassigned to ${flag.suggested_agent_name}`);
      removeFromList(flag.call_id);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Reassign failed');
    } finally {
      setBusyId(null);
    }
  }, []);

  const dismiss = useCallback(async (flag) => {
    setBusyId(flag.call_id);
    try {
      await axios.post(
        `${API}/admin/attribution-flags/${flag.call_id}/dismiss`,
        {}, { timeout: 12000 },
      );
      toast.success('Dismissed');
      removeFromList(flag.call_id);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Dismiss failed');
    } finally {
      setBusyId(null);
    }
  }, []);

  // Keyboard handler mounted on the page — J/K navigate, R accept, D dismiss, O open.
  useEffect(() => {
    const onKey = (e) => {
      if (['INPUT', 'TEXTAREA', 'SELECT'].includes(document.activeElement?.tagName)) return;
      const key = e.key.toLowerCase();
      if (key === 'j' || e.key === 'ArrowDown') {
        e.preventDefault();
        setFocusIdx((i) => Math.min(i + 1, flags.length - 1));
      } else if (key === 'k' || e.key === 'ArrowUp') {
        e.preventDefault();
        setFocusIdx((i) => Math.max(i - 1, 0));
      } else if (key === 'r') {
        e.preventDefault();
        const f = flags[focusIdx];
        if (f) reassignSuggested(f);
      } else if (key === 'd') {
        e.preventDefault();
        const f = flags[focusIdx];
        if (f) dismiss(f);
      } else if (key === 'o') {
        e.preventDefault();
        const f = flags[focusIdx];
        if (f) window.open(`/calls/${f.call_id}`, '_blank', 'noopener');
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [flags, focusIdx, reassignSuggested, dismiss]);

  return (
    <div className="p-6 space-y-4" data-testid="bulk-attribution-review-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
            <AlertTriangle className="w-6 h-6 text-amber-500" />
            Attribution Review
          </h1>
          <p className="text-sm text-slate-600 mt-1">
            {flags.length} open flag{flags.length === 1 ? '' : 's'} — someone answered a call on an
            extension that isn&rsquo;t theirs. Reassign or dismiss to keep scorecards accurate.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[11px] text-slate-500 flex items-center gap-1 bg-slate-100 border border-slate-200 rounded px-2 py-1">
            <Keyboard className="w-3 h-3" />
            <kbd className="font-semibold">J/K</kbd> nav ·{' '}
            <kbd className="font-semibold">R</kbd> reassign ·{' '}
            <kbd className="font-semibold">D</kbd> dismiss ·{' '}
            <kbd className="font-semibold">O</kbd> open
          </span>
          <button
            onClick={load}
            className="text-xs font-semibold text-slate-700 border border-slate-200 rounded-lg px-3 py-1.5 hover:bg-slate-50 inline-flex items-center gap-1.5"
            data-testid="attribution-review-refresh"
          >
            <RefreshCw className="w-3.5 h-3.5" /> Refresh
          </button>
        </div>
      </div>

      {loading ? (
        <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
      ) : flags.length === 0 ? (
        <div className="py-16 text-center bg-emerald-50 border border-emerald-200 rounded-lg" data-testid="attribution-review-empty">
          <Check className="w-8 h-8 text-emerald-500 mx-auto" />
          <p className="text-emerald-900 font-semibold mt-2">Nothing to review</p>
          <p className="text-[12px] text-emerald-700">Every call&rsquo;s speaker matches the registered agent. Clean.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {flags.map((f, i) => {
            const focused = i === focusIdx;
            return (
              <div
                key={f.call_id}
                ref={(el) => { rowRefs.current[f.call_id] = el; }}
                onClick={() => setFocusIdx(i)}
                className={`rounded-lg border p-4 cursor-pointer transition-shadow ${focused ? 'border-amber-400 shadow-md ring-2 ring-amber-100' : 'border-slate-200 hover:border-slate-300'}`}
                data-testid={`attribution-review-row-${f.call_id}`}
              >
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-slate-900">
                      Transcript says &ldquo;<span className="text-amber-800">{f.detected_name}</span>&rdquo;
                      {' '}· extension registered to{' '}
                      <span className="text-slate-700">{f.registered_agent_name || `ext ${f.registered_extension}`}</span>
                    </p>
                    <p className="text-[11px] text-slate-500 mt-0.5">
                      Called {_fmtDate(f.call_date)}
                      {f.customer_phone && <> · from {f.customer_phone}</>}
                      {typeof f.duration_seconds === 'number' && <> · {Math.floor(f.duration_seconds / 60)}m{String(f.duration_seconds % 60).padStart(2, '0')}s</>}
                    </p>
                    {f.transcript_excerpt && (
                      <div className="mt-2 bg-slate-50 border-l-2 border-slate-300 pl-2 pr-1 py-1.5 text-[12px] text-slate-700 italic">
                        &ldquo;{f.transcript_excerpt}&rdquo;
                      </div>
                    )}
                  </div>
                  <Link
                    to={`/calls/${f.call_id}`}
                    target="_blank"
                    className="text-[11px] text-slate-500 hover:text-slate-800 inline-flex items-center gap-1 flex-shrink-0"
                    data-testid={`attribution-review-open-${f.call_id}`}
                  >
                    <ExternalLink className="w-3 h-3" /> open
                  </Link>
                </div>
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  {f.suggested_agent_id ? (
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); reassignSuggested(f); }}
                      disabled={busyId === f.call_id}
                      className="inline-flex items-center gap-1.5 text-xs font-semibold text-white bg-amber-600 hover:bg-amber-700 disabled:opacity-50 px-3 py-1 rounded"
                      data-testid={`attribution-review-reassign-${f.call_id}`}
                    >
                      {busyId === f.call_id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                      Reassign to {f.suggested_agent_name} (ext {f.suggested_agent_id})
                    </button>
                  ) : (
                    <span className="text-[11px] text-slate-500 italic">
                      No matching user in directory — open the call to pick manually
                    </span>
                  )}
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); dismiss(f); }}
                    disabled={busyId === f.call_id}
                    className="inline-flex items-center gap-1 text-xs font-medium text-slate-600 hover:text-slate-800"
                    data-testid={`attribution-review-dismiss-${f.call_id}`}
                  >
                    <X className="w-3 h-3" /> Keep as is
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default BulkAttributionReviewPage;
